'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { FILTER_DROPDOWN_ICON, formatOptionLabel } from '@/utils/display';

interface FilterSelectProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

export function FilterSelect({ label, value, options, onChange }: FilterSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const selectedLabel = useMemo(() => {
    const selectedOption = options.find((option) => option === value);
    return formatOptionLabel(selectedOption ?? value);
  }, [options, value]);

  return (
    <div className="flex w-full flex-col gap-1 sm:w-auto sm:min-w-40">
      <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-black">
        {label}
      </label>
      <div className="relative" ref={containerRef}>
        <button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className="w-full cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 pr-9 text-left text-sm text-slate-700 shadow-sm transition-colors hover:border-blue-200 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="block truncate">{selectedLabel}</span>
        </button>
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
          {FILTER_DROPDOWN_ICON}
        </span>

        {isOpen && (
          <div className="absolute left-0 right-0 z-50 mt-1 max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
            {options.map((option) => {
              const isSelected = option === value;

              return (
                <button
                  key={`${label}-${option}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  className={`w-full cursor-pointer rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                    isSelected
                      ? 'bg-blue-50 font-semibold text-blue-700'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {formatOptionLabel(option)}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
